import { useState } from 'react'
import {
    useClientMutations,
    useClients,
    useProjectMutations,
    useProjects,
} from '../hooks/useApi'
import { formatMoney } from '../lib/format'
import { useT, type TFunction } from '../i18n/context'
import { useActiveWorkspaceId } from '../stores/workspace'
import { toast } from '../stores/toast'
import type { Client, Project } from '../types/api'
import { Button } from '../components/ui/Button'
import { Card, CardBody } from '../components/ui/Card'
import { Dialog } from '../components/ui/Dialog'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { EmptyState } from '../components/ui/EmptyState'
import { Field, Input, Textarea } from '../components/ui/Field'
import { PageLoader } from '../components/ui/Spinner'
import { errText } from '../lib/errors'

export function ClientsPage() {
    const t = useT()
    const workspaceId = useActiveWorkspaceId()
    const clients = useClients(workspaceId)
    const projects = useProjects(workspaceId)
    const [clientDialog, setClientDialog] = useState<Client | 'new' | null>(
        null,
    )
    const [projectDialog, setProjectDialog] = useState<{
        client: Client
        project: Project | 'new'
    } | null>(null)

    const projectsByClient = new Map<number, Project[]>()
    for (const p of projects.data ?? []) {
        const list = projectsByClient.get(p.clientId) ?? []
        list.push(p)
        projectsByClient.set(p.clientId, list)
    }

    return (
        <div className='space-y-4'>
            <div className='flex justify-end'>
                <Button onClick={() => setClientDialog('new')}>
                    {t('clients.newClient')}
                </Button>
            </div>

            {clients.isLoading || projects.isLoading ? (
                <PageLoader />
            ) : (clients.data ?? []).length === 0 ? (
                <Card>
                    <EmptyState
                        title={t('clients.emptyTitle')}
                        description={t('clients.emptyDesc')}
                        action={
                            <Button onClick={() => setClientDialog('new')}>
                                {t('clients.newClient')}
                            </Button>
                        }
                    />
                </Card>
            ) : (
                <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
                    {clients.data!.map(client => (
                        <ClientCard
                            key={client.id}
                            t={t}
                            client={client}
                            projects={projectsByClient.get(client.id) ?? []}
                            workspaceId={workspaceId!}
                            onEdit={() => setClientDialog(client)}
                            onAddProject={() =>
                                setProjectDialog({
                                    client,
                                    project: 'new',
                                })
                            }
                            onEditProject={project =>
                                setProjectDialog({ client, project })
                            }
                        />
                    ))}
                </div>
            )}

            <ClientDialog
                value={clientDialog}
                workspaceId={workspaceId!}
                onClose={() => setClientDialog(null)}
            />
            <ProjectDialog
                value={projectDialog}
                workspaceId={workspaceId!}
                onClose={() => setProjectDialog(null)}
            />
        </div>
    )
}

function ClientCard({
    t,
    client,
    projects,
    workspaceId,
    onEdit,
    onAddProject,
    onEditProject,
}: {
    t: TFunction
    client: Client
    projects: Project[]
    workspaceId: number
    onEdit: () => void
    onAddProject: () => void
    onEditProject: (project: Project) => void
}) {
    const { remove } = useClientMutations(workspaceId)
    const [confirmDelete, setConfirmDelete] = useState(false)

    return (
        <Card>
            <CardBody className='space-y-3'>
                <div className='flex items-start justify-between gap-2'>
                    <div className='min-w-0'>
                        <h2 className='truncate font-semibold text-slate-800 dark:text-slate-100'>
                            {client.name}
                        </h2>
                        {client.contactDetails && (
                            <p className='mt-0.5 truncate text-xs whitespace-pre-line text-slate-500 dark:text-slate-400'>
                                {client.contactDetails}
                            </p>
                        )}
                    </div>
                    <div className='flex shrink-0 gap-1'>
                        <Button variant='ghost' size='sm' onClick={onEdit}>
                            {t('common.edit')}
                        </Button>
                        <Button
                            variant='ghost'
                            size='sm'
                            className='text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/60'
                            onClick={() => setConfirmDelete(true)}
                        >
                            {t('common.delete')}
                        </Button>
                    </div>
                </div>

                <div className='rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/60 p-3'>
                    <div className='mb-2 flex items-center justify-between'>
                        <span className='text-xs font-semibold tracking-wide text-slate-500 dark:text-slate-400 uppercase'>
                            {t('clients.projectsCount', {
                                count: projects.length,
                            })}
                        </span>
                        <Button
                            variant='ghost'
                            size='sm'
                            onClick={onAddProject}
                        >
                            {t('clients.newProject')}
                        </Button>
                    </div>
                    {projects.length === 0 ? (
                        <p className='text-xs text-slate-500 dark:text-slate-400'>
                            {t('clients.noProjects')}
                        </p>
                    ) : (
                        <ul className='divide-y divide-slate-200/70 dark:divide-slate-800'>
                            {projects.map(p => (
                                <li
                                    key={p.id}
                                    className='flex items-center justify-between gap-2 py-1.5 first:pt-0 last:pb-0'
                                >
                                    <button
                                        className='min-w-0 flex-1 truncate text-left text-sm text-slate-700 dark:text-slate-200 hover:text-indigo-700 dark:hover:text-indigo-300'
                                        onClick={() => onEditProject(p)}
                                        title={t('clients.editProjectTooltip')}
                                    >
                                        {p.name}
                                    </button>
                                    <span className='shrink-0 text-xs tabular-nums text-slate-500 dark:text-slate-400'>
                                        {p.hourlyRate !== null
                                            ? `${formatMoney(p.hourlyRate)}/h`
                                            : t('clients.noRate')}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </CardBody>
            <ConfirmDialog
                open={confirmDelete}
                message={t('clients.deleteConfirm', { name: client.name })}
                confirmLabel={t('common.delete')}
                loading={remove.isPending}
                onClose={() => setConfirmDelete(false)}
                onConfirm={() => {
                    remove.mutate(client.id, {
                        onSuccess: () => {
                            toast.success(t('clients.deleted'))
                            setConfirmDelete(false)
                        },
                        onError: err => toast.error(errText(err)),
                    })
                }}
            />
        </Card>
    )
}

function ClientDialog({
    value,
    workspaceId,
    onClose,
}: {
    value: Client | 'new' | null
    workspaceId: number
    onClose: () => void
}) {
    const t = useT()
    const { create, update } = useClientMutations(workspaceId)
    const isNew = value === 'new'
    const editing = value !== null && value !== 'new' ? value : null
    const [name, setName] = useState('')
    const [contactDetails, setContactDetails] = useState('')
    const [notes, setNotes] = useState('')
    const [address, setAddress] = useState('')
    const [vatId, setVatId] = useState('')
    const [initializedFor, setInitializedFor] = useState<number | null | 'new'>(
        null,
    )

    if (value !== null && initializedFor !== (isNew ? 'new' : editing?.id)) {
        setInitializedFor(isNew ? 'new' : editing!.id)
        setName(editing?.name ?? '')
        setContactDetails(editing?.contactDetails ?? '')
        setNotes(editing?.notes ?? '')
        setAddress(editing?.address ?? '')
        setVatId(editing?.vatId ?? '')
    }

    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        const body = {
            name,
            contactDetails,
            notes,
            address: address || undefined,
            vatId: vatId || undefined,
        }
        const opts = {
            onSuccess: () => {
                toast.success(isNew ? t('clients.created') : t('clients.saved'))
                onClose()
            },
            onError: (err: unknown) => toast.error(errText(err)),
        }
        if (isNew) create.mutate(body, opts)
        else update.mutate({ id: editing!.id, ...body }, opts)
    }

    return (
        <Dialog
            open={value !== null}
            onClose={onClose}
            title={isNew ? t('clients.createTitle') : t('clients.editTitle')}
        >
            <form onSubmit={submit} className='space-y-3'>
                <Field label={t('clients.name')}>
                    <Input
                        value={name}
                        onChange={e => setName(e.target.value)}
                        required
                        maxLength={200}
                    />
                </Field>
                <Field label={t('clients.contactDetails')}>
                    <Textarea
                        value={contactDetails}
                        onChange={e => setContactDetails(e.target.value)}
                        placeholder={t('clients.contactPlaceholder')}
                    />
                </Field>
                <Field label={t('clients.notes')}>
                    <Textarea
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                    />
                </Field>
                <Field label={t('clients.invoiceAddress')}>
                    <Textarea
                        value={address}
                        onChange={e => setAddress(e.target.value)}
                        placeholder={t('clients.addressPlaceholder')}
                    />
                </Field>
                <Field label={t('clients.vatLabel')}>
                    <Input
                        value={vatId}
                        onChange={e => setVatId(e.target.value)}
                        placeholder={t('clients.vatPlaceholder')}
                        maxLength={50}
                    />
                </Field>
                <div className='flex justify-end gap-2 pt-1'>
                    <Button type='button' variant='secondary' onClick={onClose}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        type='submit'
                        loading={create.isPending || update.isPending}
                    >
                        {t('common.save')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

function ProjectDialog({
    value,
    workspaceId,
    onClose,
}: {
    value: { client: Client; project: Project | 'new' } | null
    workspaceId: number
    onClose: () => void
}) {
    const t = useT()
    const { create, update, remove } = useProjectMutations(workspaceId)
    const isNew = value?.project === 'new'
    const editing = value && value.project !== 'new' ? value.project : null
    const [name, setName] = useState('')
    const [notes, setNotes] = useState('')
    const [rate, setRate] = useState('')
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [initializedFor, setInitializedFor] = useState<number | null | 'new'>(
        null,
    )

    const key = isNew ? 'new' : (editing?.id ?? null)
    if (value !== null && initializedFor !== key && key !== null) {
        setInitializedFor(key)
        setName(editing?.name ?? '')
        setNotes(editing?.notes ?? '')
        setRate(editing?.hourlyRate !== null && editing?.hourlyRate !== undefined ? String(editing.hourlyRate) : '')
    }

    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        if (!value) return
        const parsedRate = rate.trim() === '' ? null : Number(rate.replace(',', '.'))
        if (parsedRate !== null && !Number.isFinite(parsedRate)) {
            toast.error(t('clients.invalidRate'))
            return
        }
        const opts = {
            onSuccess: () => {
                toast.success(
                    isNew ? t('clients.projectCreated') : t('clients.projectSaved'),
                )
                onClose()
            },
            onError: (err: unknown) => toast.error(errText(err)),
        }
        if (isNew) {
            create.mutate(
                {
                    clientId: value.client.id,
                    name,
                    notes,
                    hourlyRate: parsedRate ?? undefined,
                },
                opts,
            )
        } else {
            update.mutate(
                {
                    id: editing!.id,
                    name,
                    notes,
                    hourlyRate: parsedRate,
                },
                opts,
            )
        }
    }

    return (
        <Dialog
            open={value !== null}
            onClose={onClose}
            title={
                isNew
                    ? t('clients.projectFor', { name: value?.client.name ?? '' })
                    : t('clients.editProjectTitle')
            }
        >
            <form onSubmit={submit} className='space-y-3'>
                <Field label={t('clients.name')}>
                    <Input
                        value={name}
                        onChange={e => setName(e.target.value)}
                        required
                        maxLength={200}
                    />
                </Field>
                <Field label={t('clients.notes')}>
                    <Textarea
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                    />
                </Field>
                <Field label={t('clients.hourlyRateLabel')}>
                    <Input
                        inputMode='decimal'
                        value={rate}
                        onChange={e => setRate(e.target.value)}
                        placeholder={t('clients.ratePlaceholder')}
                    />
                </Field>
                <div className='flex items-center justify-between pt-1'>
                    {!isNew && editing ? (
                        <Button
                            type='button'
                            variant='danger'
                            onClick={() => setConfirmDelete(true)}
                        >
                            {t('common.delete')}
                        </Button>
                    ) : (
                        <span />
                    )}
                    <div className='flex gap-2'>
                        <Button
                            type='button'
                            variant='secondary'
                            onClick={onClose}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type='submit'
                            loading={create.isPending || update.isPending}
                        >
                            {t('common.save')}
                        </Button>
                    </div>
                </div>
            </form>
            <ConfirmDialog
                open={confirmDelete}
                message={
                    editing
                        ? t('clients.projectDeleteConfirm', {
                              name: editing.name,
                          })
                        : ''
                }
                confirmLabel={t('common.delete')}
                loading={remove.isPending}
                onClose={() => setConfirmDelete(false)}
                onConfirm={() => {
                    if (!editing) return
                    remove.mutate(editing.id, {
                        onSuccess: () => {
                            toast.success(t('clients.projectDeleted'))
                            setConfirmDelete(false)
                            onClose()
                        },
                        onError: err => toast.error(errText(err)),
                    })
                }}
            />
        </Dialog>
    )
}
